import { useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { audit, type AuditEvent } from "@/api/audit";
import { downloadCsv } from "@/lib/csv";
import { Button, Card, CardBody, Input, Label } from "@/components/ui";

interface Filters {
  action: string;
  resource_type: string;
  actor_type: string;
  search: string;
}

const EMPTY_FILTERS: Filters = {
  action: "",
  resource_type: "",
  actor_type: "",
  search: "",
};

export function AdminAuditPage() {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [cursor, setCursor] = useState<number | undefined>(undefined);

  const params = useMemo(
    () => ({
      action: filters.action || undefined,
      resource_type: filters.resource_type || undefined,
      actor_type: filters.actor_type || undefined,
      search: filters.search || undefined,
      cursor,
      limit: 100,
    }),
    [filters, cursor],
  );

  const list = useQuery({
    queryKey: ["audit", params],
    queryFn: () => audit.list(params),
    placeholderData: keepPreviousData,
  });

  function patch<K extends keyof Filters>(key: K, v: string) {
    setFilters((f) => ({ ...f, [key]: v }));
    setCursor(undefined);
  }

  function reset() {
    setFilters(EMPTY_FILTERS);
    setCursor(undefined);
  }

  function exportCsv() {
    if (!list.data) return;
    const rows = list.data.items.map((e) => ({
      time: e.created_at,
      action: e.action,
      resource_type: e.resource_type ?? "",
      resource_id: e.resource_id ?? "",
      actor_type: e.actor_type,
      actor_id: e.actor_id ?? "",
      user_id: e.user_id ?? "",
      ip: e.ip ?? "",
      user_agent: e.user_agent ?? "",
      payload: e.payload,
    }));
    downloadCsv(rows, `audit-${Date.now()}`);
  }

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Audit log</h1>

      <Card>
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-5">
          <div>
            <Label htmlFor="f-action">Action</Label>
            <Input
              id="f-action"
              value={filters.action}
              onChange={(e) => patch("action", e.target.value)}
              placeholder="e.g. order.created"
            />
          </div>
          <div>
            <Label htmlFor="f-resource">Resource</Label>
            <Input
              id="f-resource"
              value={filters.resource_type}
              onChange={(e) => patch("resource_type", e.target.value)}
              placeholder="e.g. order"
            />
          </div>
          <div>
            <Label htmlFor="f-actor">Actor type</Label>
            <Input
              id="f-actor"
              value={filters.actor_type}
              onChange={(e) => patch("actor_type", e.target.value)}
              placeholder="user / api_key / system"
            />
          </div>
          <div>
            <Label htmlFor="f-search">Search</Label>
            <Input
              id="f-search"
              value={filters.search}
              onChange={(e) => patch("search", e.target.value)}
              placeholder="resource id / payload"
            />
          </div>
          <div className="flex items-end gap-2">
            <Button variant="secondary" onClick={reset}>
              Reset
            </Button>
            <Button variant="secondary" onClick={exportCsv} disabled={!list.data?.items.length}>
              Export CSV
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="overflow-x-auto p-0">
          {list.isLoading ? (
            <p className="p-4 text-sm text-slate-500">Loading…</p>
          ) : list.isError ? (
            <p className="p-4 text-sm text-red-500">Failed to load audit log.</p>
          ) : !list.data || list.data.items.length === 0 ? (
            <p className="p-4 text-sm text-slate-500">No matching events.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-left uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">Time</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Resource</th>
                  <th className="px-3 py-2">Actor</th>
                  <th className="px-3 py-2">IP</th>
                  <th className="px-3 py-2">Payload</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((e: AuditEvent) => (
                  <tr key={e.id} className="border-t border-slate-200 dark:border-slate-800 align-top">
                    <td className="px-3 py-2 tnum text-slate-500">
                      {new Date(e.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 font-medium">{e.action}</td>
                    <td className="px-3 py-2">
                      <div>{e.resource_type ?? "—"}</div>
                      {e.resource_id && (
                        <div className="text-2xs text-slate-500">{e.resource_id}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div>{e.actor_type}</div>
                      {e.actor_id && <div className="text-2xs text-slate-500">{e.actor_id}</div>}
                    </td>
                    <td className="px-3 py-2 tnum text-slate-500">{e.ip ?? "—"}</td>
                    <td className="px-3 py-2">
                      <pre className="max-w-md whitespace-pre-wrap break-words font-mono text-2xs text-slate-500">
                        {Object.keys(e.payload).length ? JSON.stringify(e.payload) : "—"}
                      </pre>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {list.data?.next_cursor != null && (
        <div className="flex justify-center">
          <Button variant="secondary" onClick={() => setCursor(list.data!.next_cursor!)}>
            Load older
          </Button>
        </div>
      )}
    </div>
  );
}
